'use strict';
// public/v3/src/lib/lab-orders-form.js — pure helpers for the Lab Orders page
// (pages/lab-orders.jsx), the web port of the native 3-mode surface
// (ios/.../FeatureQuest/LabOrderModel.swift). CJS like quest-homebound-form.js
// so unit tests can require it directly.
//
// Server contract: server/routes/labOrders.js (vendors / ereq / print/preview
// / print/send / sets) + server/routes/questHomebound.js resolve-dx.

const { parseIcd10 } = require('./quest-homebound-form.js');

// ---- Page mode ------------------------------------------------------------

// The three order modes. The CHOICE is a UI preference (not PHI) so it lives
// in localStorage under MODE_STORAGE_KEY; form contents never persist.
const MODES = ['ereq', 'print', 'questhome'];
const MODE_LABELS = {
  ereq: 'e-Req draft',
  print: 'Print & fax',
  questhome: 'Quest home draw',
};
const MODE_STORAGE_KEY = 'ccm.v3.labOrdersMode';

function sanitizeMode(value) {
  return MODES.includes(value) ? value : 'ereq';
}

// ---- Fasting (Elation Patient Condition / FastingMethodEnum) ---------------

const FASTING_OPTIONS = [
  { key: 'fasting_12_hour', label: 'Fasting — 12 hours' },
  { key: 'fasting_8_hours', label: 'Fasting — 8 hours' },
  { key: 'fasting_2_hours', label: 'Fasting — 2 hours' },
  { key: 'fasting_none', label: 'Non-fasting' },
  { key: 'fasting_random', label: 'Fasting not required (random)' },
];

function fastingLabel(key) {
  const hit = FASTING_OPTIONS.find(o => o.key === key);
  return hit ? hit.label : String(key || '');
}

// ---- Dx chips (resolve-dx vocabulary — QuestModel parity) ------------------

// A hand-picked alternate's tier mirrors the chat engine's rule
// (orderAutoDx.confidenceFor): covered-from-chart/search → high, other
// covered → medium, anything else → low.
function pickedConfidence(candidate) {
  if (!candidate || candidate.status !== 'covered') return 'low';
  return (candidate.source === 'chart' || candidate.source === 'search') ? 'high' : 'medium';
}

// Badge ← coverage status (the same vocabulary the Order Chat card speaks).
function statusBadge(status) {
  switch (status) {
    case 'covered': return { text: 'Covered', tone: 'ok' };
    case 'suggested': return { text: 'Suggested · verify', tone: 'warn' };
    case 'not_listed': return { text: 'Not on covered list', tone: 'warn' };
    case 'no_med_nec': return { text: 'Verify necessity', tone: 'warn' };
    case 'denied': return { text: 'Not covered', tone: 'danger' };
    case 'order_dx': return { text: 'Order dx', tone: 'ok' };
    default: return { text: 'Unverified', tone: 'muted' };
  }
}

function dxCandidateLabel(candidate) {
  if (!candidate) return '';
  return candidate.description ? `${candidate.code} — ${candidate.description}` : String(candidate.code || '');
}

// ---- Rows -----------------------------------------------------------------

// A shared-form row (modes 1 & 2):
//   { id, name, icd10, testId?, code?, compendium?, questions: [],
//     dx?, dxResolvedForName?, dxPending?, chosenDx? }
// A set-imported row carries its Elation test id (+ code/compendium/AOE
// questions); editing its name demotes it to free text.

function isRealRow(row) {
  return !!(row && String(row.name || '').trim());
}

function realRows(rows) {
  return (Array.isArray(rows) ? rows : []).filter(isRealRow);
}

// One row's ICD-10s: the chosen chip's code first, then the manual comma
// list, deduped case-insensitively (native LabOrderModel.codes(for:) parity).
function rowCodes(row) {
  const codes = [];
  const push = (code) => {
    const c = String(code || '').trim();
    if (!c) return;
    if (!codes.some(x => x.toLowerCase() === c.toLowerCase())) codes.push(c);
  };
  if (row && row.chosenDx && row.chosenDx.code) push(row.chosenDx.code);
  for (const manual of parseIcd10(row && row.icd10)) push(manual);
  return codes;
}

// Local validation for the shared form (server re-validates everything).
function validateLabForm(state) {
  const errors = [];
  if (!state || typeof state !== 'object') return { ok: false, errors: ['invalid form'] };
  if (!state.pinnedPatient || !state.pinnedPatient.elationId) errors.push('Pick a patient.');
  if (!state.vendorId) errors.push('Pick a lab.');
  const real = realRows(state.rows);
  if (!real.length) errors.push('Add at least one test with a name.');
  // ICD-10s pool across the order (quest-form rule): at least one overall.
  if (real.length && !real.some(r => rowCodes(r).length > 0)) {
    errors.push('Enter at least one ICD-10 code.');
  }
  return { ok: errors.length === 0, errors };
}

// Rows → the /ereq and /print/* `tests` payload. A set-imported row keeps its
// Elation test id verbatim; free text rides as {name} for server resolution.
function buildOrderTests(rows) {
  return realRows(rows).map(row => ({
    name: String(row.name).trim(),
    testId: row.testId || undefined,
    code: row.code || undefined,
    compendium: row.compendium || undefined,
    icd10: rowCodes(row),
  }));
}

// Import a set's TEST LIST ONLY (the server proxy already stripped all dx and
// fasting): blank rows drop, named rows keep their place, duplicates (by test
// id or case-insensitive name) are skipped. `toRow(test)` builds a fresh row
// in the caller's shape; dx re-resolves against the pinned chart afterwards.
function importSetIntoRows(rows, set, toRow) {
  const kept = realRows(rows).slice();
  for (const test of (set && Array.isArray(set.tests) ? set.tests : [])) {
    const name = String((test && test.name) || '').trim();
    if (!name) continue;
    const duplicate = kept.some(r =>
      (r.testId && test.id && String(r.testId) === String(test.id)) ||
      String(r.name || '').trim().toLowerCase() === name.toLowerCase());
    if (duplicate) continue;
    kept.push(toRow(test));
  }
  return kept;
}

// The current rows as a 'Save current as set…' payload — name + id only
// (the server enforces the tests-only strip regardless).
function currentSetTests(rows) {
  return realRows(rows).map(row => ({
    name: String(row.name).trim(),
    testId: row.testId || undefined,
  }));
}

// Required AOE questions carried by set-imported rows (per test) — collected
// on the e-req review step (the order writer accepts top-level answers).
function requiredAOE(rows) {
  const out = [];
  for (const row of realRows(rows)) {
    if (!row.testId) continue;
    for (const question of (Array.isArray(row.questions) ? row.questions : [])) {
      if (question && question.required) {
        out.push({ testId: row.testId, testName: String(row.name).trim(), question });
      }
    }
  }
  return out;
}

// ---- Order sets -----------------------------------------------------------

// Sets whose lab_vendor matches the given vendor — the chips row's scope.
function setsForVendor(sets, vendorId) {
  if (vendorId == null || vendorId === '') return [];
  return (Array.isArray(sets) ? sets : [])
    .filter(s => s && String(s.labVendorId || '') === String(vendorId));
}

// ---- Quest home draw (mode 3) ----------------------------------------------

// Quest rows keep the original quest-homebound-form shape; the dx chip merges
// into the manual icd10 string ONLY at validate/submit time so
// validateForm/buildPayload stay byte-identical to the original form.
function questTestsWithChips(tests) {
  return (Array.isArray(tests) ? tests : []).map(t => ({
    ...t,
    icd10: rowCodes(t).join(', '),
  }));
}

module.exports = {
  MODES,
  MODE_LABELS,
  MODE_STORAGE_KEY,
  sanitizeMode,
  FASTING_OPTIONS,
  fastingLabel,
  pickedConfidence,
  statusBadge,
  dxCandidateLabel,
  isRealRow,
  realRows,
  rowCodes,
  validateLabForm,
  buildOrderTests,
  importSetIntoRows,
  currentSetTests,
  requiredAOE,
  setsForVendor,
  questTestsWithChips,
};
